import React, { ForwardRefRenderFunction, forwardRef, useMemo } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { DimensionEnums } from "models/enums";
import { QuotesFormTypes } from "models/formTypes";
import { QuotationDetailsFormContainerProps } from "models/propTypes/quotationPropTypes";
//#endregion
//#region components
import QuotationDetailsForm from "./detailsForm";
import { useDimensionMaps } from "components/common";
import { useInvoiceService, useQuotesService } from "serviceHooks";
//#endregion
import * as Yup from "yup";

const QuotationDetailsFormContainer: ForwardRefRenderFunction<FormHandles, QuotationDetailsFormContainerProps> = (
  props,
  ref
) => {
  const quotesService = useQuotesService();
  const invoiceService = useInvoiceService();

  const { quotation } = props;
  const currency = quotation.currency ?? "";
  const dimensionAmount = +quotation.totalAmount;

  const { dimensions } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () =>
    formRef.current
      ? {
          ...formRef.current,
        }
      : ({} as FormHandles)
  );

  const initialValues = useMemo((): QuotesFormTypes.Details => {
    const quotationForm: QuotesFormTypes.Details = {
      customerName: quotation.customerName,
      customerOrgNr: quotation.customerOrgNo,
      referencePerson: quotation.customerReference?.referenceName,
      contactPerson: quotation.contactPerson,
      amountIncludingVat: quotation.totalAmount,
      amountExcludingVat: (quotation.rows ?? []).map((p) => (p.price ?? 0) * p.quantity).reduce((a, b) => +a + +b, 0),
      vat: quotation.totalTax,
      currency: quotation.currency,
      dimensions: invoiceService.getInvoiceDimensionData(dimensionAmount, quotation.dimensions),
    };
    quotationForm.quotationDate = quotation.date;
    quotationForm.expireDate = quotation.endDate;

    return quotationForm;
  }, [quotation]);

  const handleSubmit = async (values: QuotesFormTypes.Details) => {
    await quotesService.updateDimensions(props.quotation.serialNumber, quotesService.getQuotationDimensions(values));
    return props.quotation.serialNumber;
  };

  const validationSchema = useMemo(
    () =>
      Yup.object<QuotesFormTypes.Details>({
        customerName: Yup.string(),
        customerOrgNr: Yup.string(),
        referencePerson: Yup.string(),
        contactPerson: Yup.string(),
        amountIncludingVat: Yup.number(),
        amountExcludingVat: Yup.number(),
        vat: Yup.number(),
        currency: Yup.string(),
        quotationDate: Yup.date(),
        expireDate: Yup.date(),
        bg: Yup.string(),
        ocr: Yup.number(),
        dimensions: Yup.array()
          .of(
            Yup.object({
              dimensionId: Yup.string(),
              amount: Yup.number(),
            })
          )
          .validateDimensionAmounts(dimensions, dimensionAmount),
      }),
    []
  );

  return (
    <QuotationDetailsForm
      ref={formRef}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      quotation={quotation}
      currency={currency}
      dimensionAmount={dimensionAmount}
      handleSubmit={handleSubmit}
    />
  );
};

export default forwardRef(QuotationDetailsFormContainer);
